import type { dynamicObj } from 'src/pages/assets';
import { i18n } from 'src/boot/i18n';
export function toPositiveInt(value: any, fallback: number): number {
  // Coerce a possibly-undefined/non-numeric value (e.g. a missing user
  // preference) into a positive integer. Guards pagination math against NaN
  // leaking into API query params (DOCB-12859).
  const num = Math.floor(Number(value));
  return Number.isFinite(num) && num > 0 ? num : fallback;
}

export function isUndefinedOrNull(value: any) {
  let isValid = true;
  try {
    isValid =
      value !== 'undefined' &&
      typeof value !== 'undefined' &&
      value !== null &&
      value !== 'null'
        ? false
        : true;
  } catch (e) {
    isValid = true;
    logError(e);
  }

  return isValid;
}

export function objLen(value: any) {
  let len = 0;
  try {
    if (isObject(value)) {
      len = Object.keys(value).length;
    }
  } catch (e) {
    len = 0;
    logError(e);
  }

  return len;
}

export function tryParseJSONObject(jsonString: string) {
  try {
    const o = JSON.parse(jsonString);

    // Handle non-exception-throwing cases:
    // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
    // but... JSON.parse(null) returns null, and typeof null === "object",
    // so we must check for that, too. Thankfully, null is falsey, so this suffices:
    if (o && typeof o === 'object') {
      return o as dynamicObj;
    }
  } catch (e) {}

  return '';
}

export function isObject(value: any) {
  let isValid = false;
  try {
    isValid = typeof value === 'object' && value !== null;
  } catch (e) {
    isValid = false;
    logError(e);
  }

  return isValid;
}

export function isEmptyString(value: any) {
  let isValid = true as boolean;
  try {
    isValid = !(
      (typeof value === 'string' || value instanceof String) &&
      value != ''
    );
  } catch (e) {
    isValid = true;
    logError(e);
  }

  return isValid;
}

export function hasValue(value: any) {
  let isValid = true as boolean;
  try {
    isValid = !isUndefinedOrNull(value) && !isEmptyString(value);
  } catch (e) {
    isValid = true;
    logError(e);
  }

  return isValid;
}

export function isObjectEmtpy(obj: any) {
  let isValid = false as boolean;
  try {
    isValid =
      isObject(obj) &&
      Object.keys(obj).length === 0 &&
      Object.getPrototypeOf(obj) === Object.prototype;
  } catch (e) {
    isValid = true;
    logError(e);
  }

  return isValid;
}

export function removeSpacesAndUnderscore(value: string) {
  if (!value) {
    return '';
  }
  return value.replace(/[\s-]/g, '');
}

export function removeSpacesWithUnderscore(value: string) {
  return value.replace(/ /g, '_');
}
// only allow characters, numbers, spaces, hyphen and underscore
export function validateString(value: string) {
  const re = /^[\w\d\s-\_]+$/;
  if (!re.test(value)) {
    return false;
  }
  return true;
}

// only allow characters, numbers, hyphen and underscore
export function stringWithoutSpaces(value: string) {
  const re = /^[\w\d-\_]+$/;
  if (!re.test(value)) {
    return false;
  }
  return true;
}

// only allow characters, numbers
export function onlyNumbersAndCharacters(value: string) {
  const re = /^[a-zA-Z0-9\_]+$/;
  if (!re.test(value)) {
    return false;
  }
  return true;
}

// only allow characters, numbers and no spaces
export function onlyNumbersCharactersSpaces(value: string) {
  const re = /^[\w\d\s]+$/;
  if (!re.test(value)) {
    return false;
  }
  return true;
}

// only allow characters and underscore
export function onlyCharactersAndunderscore(value: string) {
  const re = /^[\w\_]+$/;
  if (!re.test(value)) {
    return false;
  }
  return true;
}

export function allowLettersNumberDashes(value: string) {
  const re = /^[\w\d-]+$/;
  if (!re.test(value)) {
    return false;
  }
  return true;
}

export function get_field_data_types() {
  return [
    { label: i18n.t('amount'), value: 'AMOUNT' },
    { label: 'STRING', value: 'STRING' },
    { label: i18n.t('invoice_date'), value: 'DATE' },
    { label: i18n.t('number'), value: 'NUMBER' },
    { label: 'BOOLEAN', value: 'BOOLEAN' },
    { label: i18n.t('currency'), value: 'CURRENCY' },
  ];
}

export function encodeQueryData(data: any) {
  const ret: string[] = [];
  for (const d in data)
    ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
  return ret.join('&');
}

export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
}

export function getStatusIcon(row: dynamicObj, type: string): [string, string] {
  if (type === 'inbound_emails' && row.attachment_names === 'no_attachments') {
    return ['remove_circle', 'yellow'];
  }

  if (
    type === 'email_import' &&
    row.success_count === 0 &&
    row.failed_count === 0
  ) {
    return ['remove_circle', 'yellow'];
  }

  return row.success ? ['check_circle', 'green'] : ['cancel', 'red'];
}
